import { useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    CssBaseline,
    Divider,
    IconButton,
    List,
    Skeleton,
    ThemeProvider,
    Tooltip,
    Typography,
} from "@mui/material";

import { ArrowLeft, BarChart3, FileText, PanelLeftClose, PanelLeftOpen, RefreshCw, User } from "lucide-react";
import { DynamicIcon } from "lucide-react/dynamic";
import type { IconName } from "lucide-react/dynamic";

import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { DialogsProvider } from "@toolpad/core/useDialogs";

import { RootState } from "@store/store";
import { bpmV2Theme } from "@themes/Bpm";
import { AppButton } from "@components/v2/AppButton";
import { DownloadMenuButton } from "@components/v2/DownloadMenuButton";
import { ComentariosButton } from "@components/v2/ComentariosButton";
import { ModeToggle } from "@components/v2/ModeToggle";
import { TopbarTitle } from "@components/v2/TopbarTitle";
import { TopbarProfile } from "@components/v2/TopbarProfile";
import { NotificationsBell } from "@components/Notifications/NotificationsBell";
import { DialogManager } from "@components/v2/DialogManager";
import { PopupManager } from "@components/v2/PopupManager";
import { SidebarGroupLabel, SidebarNavItem } from "@components/v2/SidebarNavItem";
import { useTokenExpirationGuard } from "@hooks/useTokenExpirationGuard";
import { useGetPermission } from "@hooks/useGetPermission";
import { useReloadSection } from "@hooks/useReloadSection";
import { useLazyGetTaxSystemQuery } from "@services/api/catalogs";
import { useLazyGetCompanyQuery } from "@services/api/companies";
import { setCleanApplicationSelected } from "@store/slices/componentsSlice";

interface SidebarItemConfig {
    label: string;
    icon: React.ReactNode;
    /** V1 route segment under `/solicitud/:companyName/:id/<segment>` — undefined = no page yet. */
    segment?: string;
    /** Segment under `/v2/solicitud/:companyName/:id/<segment>` once a screen has its own v2 page — takes priority over `segment`. Empty string = the v2 index route. */
    v2Segment?: string;
    permissionCode: string;
}

interface SidebarGroupConfig {
    label: string;
    statusEnabled: Array<String>;
    items: SidebarItemConfig[];
}

/** Convierte el nombre PascalCase del icono devuelto por el backend (ej. "ListChecks") al slug kebab-case que espera `DynamicIcon` (ej. "list-checks"). */
const toKebabCase = (value: string) => value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

interface SidebarItemButtonProps {
    item: SidebarItemConfig;
    /** Ya resuelto por el padre: ¿la etapa (grupo) de este ítem fue alcanzada o superada? */
    groupEnabled: boolean;
    selected: boolean;
    collapsed: boolean;
    onClick: () => void;
}

/**
 * Un ítem del sidebar habilitado sólo si el usuario tiene asignado el
 * permiso (`item.permissionCode`, vía `useGetPermission`) con `can_watch`
 * en true Y su grupo/etapa ya fue alcanzado (`groupEnabled`, resuelto en
 * `SolicitudLayoutV2` comparando el estatus actual contra el orden de
 * `sidebarGroups` — ver ese cálculo para el porqué no basta con "el estatus
 * está en `statusEnabled` de ESTE grupo"). Se separa en su propio componente
 * (en vez de llamar `useGetPermission` dentro del `.map()` del padre) para
 * respetar las reglas de hooks — cada instancia de este componente llama el
 * hook de forma incondicional en su propio nivel.
 */
const SidebarItemButton = ({ item, groupEnabled, selected, collapsed, onClick }: SidebarItemButtonProps) => {
    const permission = useGetPermission(item.permissionCode);

    const hasPermission = permission !== undefined && permission.can_watch !== false;
    const disabled = !hasPermission || !groupEnabled;

    return (
        <SidebarNavItem
            icon={item.icon}
            label={collapsed ? "" : item.label}
            selected={selected}
            disabled={disabled}
            onClick={disabled ? undefined : onClick}
        />
    );
};

/**
 * Full BPM v2 shell (topbar + sidebar), built from `originacion.html`/
 * `originacion-pf.html`. Sidebar items with a `v2Segment` route within this
 * shell; the rest still navigate out to the existing (legacy-themed) routes
 * under `/solicitud/:companyName/:id/...` since those screens haven't been
 * migrated yet.
 */
export const SolicitudLayoutV2 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const dispatch = useDispatch();
    const { reloadSection: reloadCurrentSection } = useReloadSection();

    const [collapsed, setCollapsed] = useState(false);
    const [sidebarGroups, setSidebarGroups] = useState<SidebarGroupConfig[]>([]);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);

    useTokenExpirationGuard();

    const userSession = useSelector((state: RootState) => state.session.user);
    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);
    const reloadSection = useSelector((state: RootState) => state.components.currentSection.reload);

    const permissionDownloadDocsApp = useGetPermission("ACCDR");
    const permissionGeneralComments = useGetPermission("MCOGRAL");
    const permissionReportsApp = useGetPermission("MREPORT");

    const [triggerGetTaxSystem] = useLazyGetTaxSystemQuery();
    const [triggerCompanyInfo, { isLoading: isLoadingCompanyInfo }] = useLazyGetCompanyQuery();

    const initials =
        `${userSession.name?.charAt(0) ?? ""}${userSession.last_name?.charAt(0) ?? ""}`.toUpperCase() || "??";
    const folio = String(applicationSelected.id_application_company ?? "").padStart(5, "0");

    useEffect(() => {
        if (applicationSelected.tax_system.id === "0-0-0-0-0") return;

        const loadSidebarGroups = async () => {
            const taxSystemInfo = await triggerGetTaxSystem(applicationSelected.tax_system.id, true).unwrap();

            const groups: SidebarGroupConfig[] = taxSystemInfo.config_extra.bpm_lateral_options_v2.map((group) => ({
                label: group.label,
                statusEnabled: group.statusEnabled,
                items: group.items.map((item) => ({
                    label: item.label,
                    icon: <DynamicIcon name={toKebabCase(item.icon) as IconName} size={16} />,
                    segment: item.segment,
                    v2Segment: item.v2Segment,
                    permissionCode: item.permissionCode,
                })),
            }));

            setSidebarGroups(groups);
        };

        loadSidebarGroups();
    }, [applicationSelected.tax_system.id]);

    useEffect(() => {
        if (applicationSelected.company_id === "0-0-0-0-0") return;

        const loadCompanyLogo = async () => {
            const company = await triggerCompanyInfo(applicationSelected.company_id).unwrap();
            const image = company.palette.interface.navbar.logo;

            setLogoUrl(image == null ? null : `${import.meta.env.VITE_URL_STATIC_RESOURCES}/${image}`);
        };

        loadCompanyLogo();
    }, [applicationSelected.company_id]);

    // `sidebarGroups` está ordenado cronológicamente (Solicitud → Evaluación →
    // Operación). El estatus actual pertenece a UN grupo (su etapa actual),
    // pero ese grupo Y todos los anteriores ya fueron alcanzados y deben
    // seguir habilitados — sólo los grupos posteriores (etapas futuras) se
    // deshabilitan. Ej.: en CSAOL (etapa "Evaluación"), "Solicitud" también
    // debe estar habilitado.
    const currentGroupIndex = sidebarGroups.findIndex((group) =>
        group.statusEnabled.includes(applicationSelected.status_code),
    );

    const v2Base = `/v2/solicitud/${params.companyName}/${params.id}`;
    const isItemActive = (item: SidebarItemConfig) =>
        item.v2Segment !== undefined && location.pathname === `${v2Base}/${item.v2Segment}`.replace(/\/$/, "");

    const goToScreen = (item: SidebarItemConfig) => {
        if (item.v2Segment !== undefined) {
            navigate(`${v2Base}/${item.v2Segment}`.replace(/\/$/, ""));
        } else if (item.segment) {
            navigate(`/solicitud/${params.companyName}/${params.id}/${item.segment}`);
        }
    };

    return (
        <ThemeProvider theme={bpmV2Theme}>
            <CssBaseline />
            <DialogsProvider>
                <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
                    {/* Topbar */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2.5,
                            flexShrink: 0,
                            height: 64,
                            px: 3,
                            backgroundColor: "background.paper",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Tooltip title="Volver a la bandeja de solicitudes">
                            <IconButton
                                onClick={() => {
                                    dispatch(setCleanApplicationSelected());
                                    navigate("/v2/solicitudes");
                                }}
                                sx={{ color: "primary.main" }}
                            >
                                <ArrowLeft size={18} />
                            </IconButton>
                        </Tooltip>
                        {isLoadingCompanyInfo ? (
                            <Skeleton variant="rectangular" width={110} height={32} sx={{ flexShrink: 0 }} />
                        ) : (
                            <Box
                                component="img"
                                src={logoUrl ?? undefined}
                                sx={{ width: 110, flexShrink: 0 }}
                            />
                        )}
                        <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />
                        <TopbarTitle
                            ident={applicationSelected.company_rfc ?? "—"}
                            title={applicationSelected.company_name ?? applicationSelected.user_name ?? ""}
                        />
                        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
                            <ModeToggle
                                options={[
                                    { value: "solicitud", label: "Solicitud", icon: <FileText size={13} /> },
                                    { value: "cliente", label: "Cliente", icon: <User size={13} /> },
                                ]}
                                value="solicitud"
                                onChange={() => {}}
                            />
                            <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />
                            {permissionDownloadDocsApp?.can_watch === true && <DownloadMenuButton />}
                            {permissionGeneralComments?.can_watch === true && <ComentariosButton />}
                            {permissionReportsApp?.can_watch === true && (
                                <AppButton
                                    tone="ghost"
                                    startIcon={<BarChart3 size={14} />}
                                    sx={{ height: 34, fontSize: "0.75rem" }}
                                    onClick={() => navigate("/v2/reportes")}
                                >
                                    Reportes
                                </AppButton>
                            )}
                            <Tooltip title="Recargar sección">
                                <IconButton
                                    onClick={() => reloadCurrentSection()}
                                    loading={reloadSection}
                                    loadingIndicator={<CircularProgress size={14} />}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        width: 34,
                                        height: 34,
                                    }}
                                >
                                    <RefreshCw size={14} />
                                </IconButton>
                            </Tooltip>
                            {userSession.preferences.options?.showNotifications === true && (
                                <NotificationsBell
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        width: 34,
                                        height: 34,
                                    }}
                                    iconSize={14}
                                />
                            )}
                            <TopbarProfile initials={initials} withAccountMenu />
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                        {/* Sidebar */}
                        <Box
                            sx={{
                                width: collapsed ? 64 : 240,
                                flexShrink: 0,
                                display: "flex",
                                flexDirection: "column",
                                backgroundColor: "background.paper",
                                borderRight: "1px solid",
                                borderColor: "divider",
                                overflowY: "auto",
                                transition: "width 140ms ease",
                                py: 1,
                            }}
                        >
                            {!collapsed && (
                                <Box sx={{ px: 2.5, pb: 1 }}>
                                    <Typography
                                        sx={{ typography: "overline", fontSize: "0.625rem", color: "text.disabled" }}
                                    >
                                        Solicitud
                                    </Typography>
                                    <Box>
                                        <Typography
                                            component="span"
                                            sx={{
                                                display: "inline-block",
                                                mt: 0.5,
                                                fontFamily: "monospace",
                                                fontSize: "0.75rem",
                                                fontWeight: 700,
                                                color: "success.dark",
                                                backgroundColor: "success.light",
                                                px: 1.25,
                                                py: 0.5,
                                                borderRadius: 999,
                                            }}
                                        >
                                            {folio}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {sidebarGroups.map((group, groupIndex) => (
                                <Box key={group.label}>
                                    {!collapsed && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
                                    <List disablePadding>
                                        {group.items.map((item) => (
                                            <SidebarItemButton
                                                key={item.label}
                                                item={item}
                                                groupEnabled={
                                                    currentGroupIndex !== -1 && groupIndex <= currentGroupIndex
                                                }
                                                selected={isItemActive(item)}
                                                collapsed={collapsed}
                                                onClick={() => goToScreen(item)}
                                            />
                                        ))}
                                    </List>
                                </Box>
                            ))}

                            <Box
                                sx={{
                                    mt: "auto",
                                    px: collapsed ? 0 : 2,
                                    pt: 1.5,
                                    display: "flex",
                                    justifyContent: collapsed ? "center" : "flex-start",
                                }}
                            >
                                <Tooltip title={collapsed ? "Expandir menú" : "Colapsar menú"}>
                                    <IconButton
                                        onClick={() => setCollapsed((prev) => !prev)}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            gap: 1,
                                            px: collapsed ? 1 : 1.5,
                                            height: 32,
                                        }}
                                    >
                                        {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
                                        {!collapsed && (
                                            <Typography sx={{ typography: "button", fontSize: "0.6875rem" }}>
                                                Colapsar
                                            </Typography>
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Main content */}
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", backgroundColor: "background.default" }}>
                            <Box sx={{ maxWidth: 1480, mx: "auto", px: 5, py: 3 }}>
                                <Outlet />
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <DialogManager />
                <PopupManager />
            </DialogsProvider>
        </ThemeProvider>
    );
};
